import type {
  FinanceDataReliability,
  MarginLeakageStageSummary,
  ProjectMarginLeakageBreakdown,
  Task,
  TaskFinancialStage,
  TaskFinancialStageSnapshot,
  TaskFinancialState,
} from "../models/production";

interface ProjectMarginLeakageTaskEntry {
  task: Pick<Task, "id" | "title" | "taskType" | "bidHours">;
  financialState?: TaskFinancialState;
}

interface BuildProjectMarginLeakageArgs {
  projectId: string;
  projectName: string;
  currency?: string;
  tasks: ProjectMarginLeakageTaskEntry[];
}

type LeakagePoint = {
  labourCost?: number;
  grossMargin?: number;
  lowConfidence: boolean;
};

function roundMoney(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 100) / 100;
}

function roundPercent(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return Math.round(value * 10) / 10;
}

function calculateMarginPct(grossMargin?: number, bidValue?: number) {
  if (typeof grossMargin !== "number" || typeof bidValue !== "number" || !Number.isFinite(grossMargin) || !Number.isFinite(bidValue) || bidValue <= 0) {
    return undefined;
  }
  return roundPercent((grossMargin / bidValue) * 100);
}

function getStageSnapshot(state: TaskFinancialState | undefined, stage: TaskFinancialStage): TaskFinancialStageSnapshot | undefined {
  return state?.stageSnapshots?.find((snapshot) => snapshot.stage === stage);
}

function getTaskBidValue(state?: TaskFinancialState) {
  return typeof state?.taskBidValueInMoney === "number" && Number.isFinite(state.taskBidValueInMoney) ? state.taskBidValueInMoney : undefined;
}

function isLowConfidence(state?: TaskFinancialState) {
  return state?.financeReliability === "low";
}

function resolveAssignmentPoint(state?: TaskFinancialState): LeakagePoint {
  const assigned = getStageSnapshot(state, "assigned_artist_forecast");
  const initial = getStageSnapshot(state, "initial_bid_forecast");
  const source = assigned || initial;
  return {
    labourCost: roundMoney(source?.labourCost),
    grossMargin: roundMoney(source?.grossProfit),
    lowConfidence: isLowConfidence(state),
  };
}

function resolveExecutionPoint(state?: TaskFinancialState): LeakagePoint {
  const assigned = resolveAssignmentPoint(state);
  const hasActualTimelogs = (state?.actualLoggedMinutes || 0) > 0;
  const actualCost =
    typeof state?.actualLabourCost === "number" && Number.isFinite(state.actualLabourCost) ? state.actualLabourCost : undefined;
  const actualMargin =
    typeof state?.actualGrossProfit === "number" && Number.isFinite(state.actualGrossProfit) ? state.actualGrossProfit : undefined;

  // Execution is intended to represent timelog-driven movement, not later fallback or close-out corrections.
  if (!hasActualTimelogs || typeof actualCost !== "number" || typeof actualMargin !== "number" || state?.fallbackActualsUsed) {
    return assigned;
  }

  return {
    labourCost: roundMoney(actualCost),
    grossMargin: roundMoney(actualMargin),
    lowConfidence: isLowConfidence(state),
  };
}

function resolveFinalPoint(state?: TaskFinancialState): LeakagePoint {
  // Final actual should only be treated as a real endpoint when the task actually reached final_actual
  // or when we have a locked final snapshot. Otherwise we keep the execution/current point.
  if (state?.financialStage === "final_actual" || state?.finalActualSnapshot) {
    const snapshot = state.finalActualSnapshot || getStageSnapshot(state, "final_actual");
    return {
      labourCost: roundMoney(snapshot?.labourCost),
      grossMargin: roundMoney(snapshot?.grossProfit),
      lowConfidence: isLowConfidence(state),
    };
  }

  return resolveExecutionPoint(state);
}

function buildEmptyStageSummary(
  id: "initial_bid_baseline" | "assigned_forecast" | "execution" | "final_actual",
  label: string,
  helperText: string
) : MarginLeakageStageSummary {
  return {
    id,
    label,
    helperText,
    bidValue: 0,
    labourCost: 0,
    grossMargin: 0,
    grossMarginPct: 0,
    taskCount: 0,
    lowConfidenceTaskCount: 0,
  };
}

function getDirection(value?: number): "positive" | "negative" | "neutral" {
  if (typeof value !== "number" || !Number.isFinite(value) || value === 0) return "neutral";
  return value > 0 ? "positive" : "negative";
}

function percentOfInitial(value?: number, initial?: number) {
  if (typeof value !== "number" || !Number.isFinite(value) || typeof initial !== "number" || !Number.isFinite(initial) || initial === 0) {
    return undefined;
  }
  return roundPercent((value / initial) * 100);
}

function deriveReliability(tasks: ProjectMarginLeakageTaskEntry[]): FinanceDataReliability {
  const states = tasks.map((entry) => entry.financialState).filter(Boolean) as TaskFinancialState[];
  if (!states.length) return "low";
  if (states.every((state) => state.financeReliability === "high")) return "high";
  if (states.some((state) => state.financeReliability === "low")) return "low";
  return "medium";
}

export function buildProjectMarginLeakageBreakdown({
  projectId,
  projectName,
  currency,
  tasks,
}: BuildProjectMarginLeakageArgs): ProjectMarginLeakageBreakdown {
  // TODO: extend this derived analytical layer with departmental leakage splits,
  // per-artist leakage contribution, and predictive margin-risk signals. Keep those
  // future additions here so the core finance engine remains the source of truth.
  const initial = buildEmptyStageSummary(
    "initial_bid_baseline",
    "Start Position",
    "Opening gross margin using bid value minus department-average labour assumptions."
  );
  const assigned = buildEmptyStageSummary(
    "assigned_forecast",
    "Artist Assigned",
    "Gross margin after switching from department averages to the assigned artist rate."
  );
  const execution = buildEmptyStageSummary(
    "execution",
    "Artist Started Work",
    "Live gross margin once timelogs have started, comparing bid time against logged time."
  );
  const finalActual = buildEmptyStageSummary(
    "final_actual",
    "Task Complete",
    "Final gross margin once the task is complete and actual labour cost is fixed."
  );

  let totalBidValue = 0;
  let totalBidDays = 0;
  let fallbackActualTaskCount = 0;
  let lowConfidenceTaskCount = 0;
  let allTasksFinal = tasks.length > 0;

  for (const entry of tasks) {
    const state = entry.financialState;
    const bidValue = getTaskBidValue(state);
    if (typeof bidValue !== "number") {
      allTasksFinal = false;
      continue;
    }
    totalBidValue += bidValue;
    totalBidDays += state?.bidTimeAllocationDays || entry.task.bidHours || 0;

    const initialSnapshot = getStageSnapshot(state, "initial_bid_forecast");
    const assignmentPoint = resolveAssignmentPoint(state);
    const executionPoint = resolveExecutionPoint(state);
    const finalPoint = resolveFinalPoint(state);
    const isTaskLowConfidence = isLowConfidence(state);

    initial.bidValue += bidValue;
    assigned.bidValue += bidValue;
    execution.bidValue += bidValue;
    finalActual.bidValue += bidValue;

    initial.labourCost += initialSnapshot?.labourCost || 0;
    initial.grossMargin += initialSnapshot?.grossProfit || 0;
    assigned.labourCost += assignmentPoint.labourCost || 0;
    assigned.grossMargin += assignmentPoint.grossMargin || 0;
    execution.labourCost += executionPoint.labourCost || 0;
    execution.grossMargin += executionPoint.grossMargin || 0;
    finalActual.labourCost += finalPoint.labourCost || 0;
    finalActual.grossMargin += finalPoint.grossMargin || 0;

    initial.taskCount += 1;
    assigned.taskCount += 1;
    execution.taskCount += 1;
    finalActual.taskCount += 1;

    if (isTaskLowConfidence) {
      lowConfidenceTaskCount += 1;
      initial.lowConfidenceTaskCount += 1;
      assigned.lowConfidenceTaskCount += 1;
      execution.lowConfidenceTaskCount += 1;
      finalActual.lowConfidenceTaskCount += 1;
    }

    if (state?.fallbackActualsUsed) {
      fallbackActualTaskCount += 1;
    }

    if (state?.financialStage !== "final_actual") {
      allTasksFinal = false;
    }
  }

  initial.bidValue = roundMoney(initial.bidValue) || 0;
  initial.labourCost = roundMoney(initial.labourCost) || 0;
  initial.grossMargin = roundMoney(initial.grossMargin) || 0;
  initial.grossMarginPct = calculateMarginPct(initial.grossMargin, initial.bidValue) || 0;

  assigned.bidValue = roundMoney(assigned.bidValue) || 0;
  assigned.labourCost = roundMoney(assigned.labourCost) || 0;
  assigned.grossMargin = roundMoney(assigned.grossMargin) || 0;
  assigned.grossMarginPct = calculateMarginPct(assigned.grossMargin, assigned.bidValue) || 0;

  execution.bidValue = roundMoney(execution.bidValue) || 0;
  execution.labourCost = roundMoney(execution.labourCost) || 0;
  execution.grossMargin = roundMoney(execution.grossMargin) || 0;
  execution.grossMarginPct = calculateMarginPct(execution.grossMargin, execution.bidValue) || 0;

  finalActual.bidValue = roundMoney(finalActual.bidValue) || 0;
  finalActual.labourCost = roundMoney(finalActual.labourCost) || 0;
  finalActual.grossMargin = roundMoney(finalActual.grossMargin) || 0;
  finalActual.grossMarginPct = calculateMarginPct(finalActual.grossMargin, finalActual.bidValue) || 0;

  const assignmentDeltaValue = roundMoney((assigned.grossMargin || 0) - (initial.grossMargin || 0));
  const executionDeltaValue = roundMoney((execution.grossMargin || 0) - (assigned.grossMargin || 0));
  const dataDeltaValue = roundMoney((finalActual.grossMargin || 0) - (execution.grossMargin || 0));
  const totalMarginChange = roundMoney((finalActual.grossMargin || 0) - (initial.grossMargin || 0));
  const validationDifference = roundMoney(((assignmentDeltaValue || 0) + (executionDeltaValue || 0) + (dataDeltaValue || 0)) - (totalMarginChange || 0));

  const insights: ProjectMarginLeakageBreakdown["insights"] = [];

  if (typeof executionDeltaValue === "number" && executionDeltaValue < 0) {
    insights.push({
      id: "execution_loss",
      tone: "warning" as const,
      summary: `Margin reduced by ${formatCurrencyForInsight(Math.abs(executionDeltaValue), currency)} during execution.`,
      helperText: "This is the core leakage layer: actual work effort moved beyond the assigned-cost expectation.",
    });
  }

  if (typeof assignmentDeltaValue === "number" && assignmentDeltaValue !== 0) {
    insights.push({
      id: "assignment_shift",
      tone: assignmentDeltaValue < 0 ? ("warning" as const) : ("positive" as const),
      summary:
        assignmentDeltaValue < 0
          ? `Artist assignment increased cost by ${formatCurrencyForInsight(Math.abs(assignmentDeltaValue), currency)}.`
          : `Artist assignment improved margin by ${formatCurrencyForInsight(assignmentDeltaValue, currency)}.`,
      helperText: "This isolates the resource-mix effect from later production overruns.",
    });
  }

  if (fallbackActualTaskCount > 0 || lowConfidenceTaskCount > 0 || (typeof dataDeltaValue === "number" && dataDeltaValue !== 0)) {
    insights.push({
      id: "data_adjustment",
      tone: "neutral" as const,
      summary: `Data adjustment affected ${roundPercent(percentOfInitial(dataDeltaValue, initial.grossMargin)) || 0}% of initial margin.`,
      helperText: `${fallbackActualTaskCount} task${fallbackActualTaskCount === 1 ? "" : "s"} used fallback actuals and ${lowConfidenceTaskCount} task${lowConfidenceTaskCount === 1 ? "" : "s"} remain low confidence.`,
    });
  }

  if (!insights.length) {
    insights.push({
      id: "stable",
      tone: "positive" as const,
      summary: "Margin movement is currently modest across the tracked stages.",
      helperText: "This project does not yet show a strong assignment, execution, or data-adjustment driver.",
    });
  }

  return {
    projectId,
    projectName,
    currency,
    effectiveRatePerDay: totalBidDays > 0 ? roundMoney(totalBidValue / totalBidDays) : undefined,
    initialBidBaseline: initial,
    assignedForecast: assigned,
    execution,
    finalActual,
    assignmentDelta: {
      id: "assignment",
      label: "Assignment Impact",
      value: assignmentDeltaValue,
      percentOfInitialMargin: percentOfInitial(assignmentDeltaValue, initial.grossMargin),
      description: "Difference caused by switching from department-average assumptions to the assigned artist rate.",
      lowConfidenceImpactCount: assigned.lowConfidenceTaskCount,
      direction: getDirection(assignmentDeltaValue),
    },
    executionDelta: {
      id: "execution",
      label: "Execution Impact",
      value: executionDeltaValue,
      percentOfInitialMargin: percentOfInitial(executionDeltaValue, initial.grossMargin),
      description: "Timelog-driven movement versus the assigned forecast, based on bid time versus logged time.",
      lowConfidenceImpactCount: execution.lowConfidenceTaskCount,
      direction: getDirection(executionDeltaValue),
    },
    dataAdjustmentDelta: {
      id: "data_adjustment",
      label: "Close-out Adjustment",
      value: dataDeltaValue,
      percentOfInitialMargin: percentOfInitial(dataDeltaValue, initial.grossMargin),
      description: "Final close-out effect from fallback assumptions, late logging, and confidence corrections.",
      lowConfidenceImpactCount: finalActual.lowConfidenceTaskCount,
      direction: getDirection(dataDeltaValue),
    },
    totalMarginChange,
    totalMarginChangePctOfInitial: percentOfInitial(totalMarginChange, initial.grossMargin),
    allTasksFinal,
    fallbackActualTaskCount,
    lowConfidenceTaskCount,
    reliability: deriveReliability(tasks),
    validation: {
      reconciled: Math.abs(validationDifference || 0) < 0.01,
      difference: validationDifference,
    },
    insights,
    waterfallSteps: [
      {
        id: "initial",
        label: "Start Position",
        stepType: "total",
        value: initial.grossMargin,
        runningStart: 0,
        runningEnd: initial.grossMargin,
        tone: getDirection(initial.grossMargin),
        description: initial.helperText,
      },
      {
        id: "assignment",
        label: "Artist Assigned",
        stepType: "delta",
        value: assignmentDeltaValue,
        runningStart: initial.grossMargin,
        runningEnd: roundMoney((initial.grossMargin || 0) + (assignmentDeltaValue || 0)),
        tone: getDirection(assignmentDeltaValue),
        description: "How much margin moved when the assigned artist rate replaced department-average assumptions.",
      },
      {
        id: "execution",
        label: "Artist Started Work",
        stepType: "delta",
        value: executionDeltaValue,
        runningStart: assigned.grossMargin,
        runningEnd: roundMoney((assigned.grossMargin || 0) + (executionDeltaValue || 0)),
        tone: getDirection(executionDeltaValue),
        description: "Operational leakage from actual logged time compared with the bid time assumption.",
      },
      {
        id: "data_adjustment",
        label: "Close-out Adjustment",
        stepType: "delta",
        value: dataDeltaValue,
        runningStart: execution.grossMargin,
        runningEnd: roundMoney((execution.grossMargin || 0) + (dataDeltaValue || 0)),
        tone: getDirection(dataDeltaValue),
        description: "Final movement caused by fallback actuals, missing logs, and later data corrections.",
      },
      {
        id: "final",
        label: "Task Complete",
        stepType: "total",
        value: finalActual.grossMargin,
        runningStart: 0,
        runningEnd: finalActual.grossMargin,
        tone: getDirection(finalActual.grossMargin),
        description: finalActual.helperText,
      },
    ],
  };
}

function formatCurrencyForInsight(value: number, currency?: string) {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency || "GBP",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `£${Math.round(value).toLocaleString("en-GB")}`;
  }
}
